<template>
  <div class="lazy-video-wrapper" :class="wrapperClass">
    <video
      ref="lazyVideo"
      :class="[
        'lazy-video',
        {
          'lazy-loading': isLoading,
          'lazy-loaded': isLoaded,
          'lazy-error': hasError
        }
      ]"
      :poster="poster"
      :data-lazy-src="src"
      :data-lazy-type="'video'"
      :controls="controls"
      :autoplay="autoplay && isLoaded"
      :muted="muted"
      :loop="loop"
      :preload="preload"
      @loadstart="onLoadStart"
      @canplay="onCanPlay"
      @error="onError"
      @click="handleClick"
    >
      您的浏览器不支持视频播放
    </video>
    
    <!-- 播放按钮覆盖层 -->
    <div 
      v-if="showPlayButton && !isPlaying && !hasError" 
      class="play-overlay"
      @click="handlePlayClick"
    >
      <div class="play-button">
        <i class="el-icon-video-play"></i>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="isLoading && !hasError" class="lazy-loading-overlay">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
      </div>
      <div class="loading-text">视频加载中...</div>
    </div>
    
    <!-- 错误状态 -->
    <div v-if="hasError" class="lazy-error-overlay">
      <div class="error-icon">
        <i class="el-icon-video-camera"></i>
      </div>
      <div class="error-text">视频加载失败</div>
      <button class="retry-button" @click="retry">重试</button>
    </div>
  </div>
</template>

<script>
import lazyLoader from '@/utils/lazyLoad'

export default {
  name: 'LazyVideo',
  props: {
    // 视频源地址
    src: {
      type: String,
      required: true
    },
    // 视频封面图
    poster: {
      type: String,
      default: ''
    },
    // 是否显示控制条
    controls: {
      type: Boolean,
      default: true
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: false
    },
    // 是否静音
    muted: {
      type: Boolean,
      default: false
    },
    // 是否循环播放
    loop: {
      type: Boolean,
      default: false
    },
    // 预加载策略
    preload: {
      type: String,
      default: 'metadata', // none, metadata, auto
      validator: value => ['none', 'metadata', 'auto'].includes(value)
    },
    // 包装器样式类
    wrapperClass: {
      type: String,
      default: ''
    },
    // 是否显示播放按钮
    showPlayButton: {
      type: Boolean,
      default: true
    },
    // 点击播放
    clickToPlay: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      isLoading: false,
      isLoaded: false,
      hasError: false,
      isPlaying: false
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.lazyVideo) {
        lazyLoader.observe(this.$refs.lazyVideo)
      }
    })
  },
  
  beforeDestroy() {
    if (this.$refs.lazyVideo && lazyLoader.observer) {
      lazyLoader.observer.unobserve(this.$refs.lazyVideo)
    }
  },
  
  methods: {
    onLoadStart() {
      this.isLoading = true
      this.hasError = false
      this.$emit('loadstart')
    },
    
    onCanPlay() {
      this.isLoaded = true
      this.isLoading = false
      this.hasError = false
      this.$emit('canplay')
    },
    
    onError() {
      this.hasError = true
      this.isLoading = false
      this.isLoaded = false
      this.$emit('error')
    },
    
    handleClick() {
      if (this.clickToPlay && !this.isPlaying) {
        this.play()
      }
    },
    
    handlePlayClick() {
      this.play()
    },
    
    play() {
      if (this.$refs.lazyVideo && this.isLoaded) {
        this.$refs.lazyVideo.play()
        this.isPlaying = true
        this.$emit('play')
      }
    },
    
    pause() {
      if (this.$refs.lazyVideo) {
        this.$refs.lazyVideo.pause()
        this.isPlaying = false
        this.$emit('pause')
      }
    },
    
    retry() {
      this.hasError = false
      this.isLoaded = false
      this.isLoading = false
      
      if (this.$refs.lazyVideo) {
        lazyLoader.observe(this.$refs.lazyVideo)
      }
    },
    
    // 获取视频元素
    getVideoElement() {
      return this.$refs.lazyVideo
    }
  }
}
</script>

<style lang="less" scoped>
.lazy-video-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #000;
  
  .lazy-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.3s ease;
    
    &.lazy-loading {
      opacity: 0.8;
    }
    
    &.lazy-loaded {
      opacity: 1;
    }
    
    &.lazy-error {
      opacity: 0.3;
    }
  }
  
  .play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    cursor: pointer;
    transition: opacity 0.3s ease;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
      
      .play-button {
        transform: scale(1.1);
      }
    }
    
    .play-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.3s ease;
      
      i {
        font-size: 24px;
        color: #333;
        margin-left: 3px; // 视觉居中调整
      }
    }
  }
  
  .lazy-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    
    .loading-spinner {
      margin-bottom: 16px;
      
      .spinner-ring {
        width: 32px;
        height: 32px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top: 3px solid #fff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
    
    .loading-text {
      font-size: 14px;
      opacity: 0.8;
    }
  }
  
  .lazy-error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    
    .error-icon {
      margin-bottom: 12px;
      
      i {
        font-size: 48px;
        opacity: 0.6;
      }
    }
    
    .error-text {
      font-size: 14px;
      margin-bottom: 16px;
      opacity: 0.8;
    }
    
    .retry-button {
      padding: 8px 16px;
      background-color: #409eff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      transition: background-color 0.3s ease;
      
      &:hover {
        background-color: #66b1ff;
      }
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 响应式样式
@media (max-width: 768px) {
  .lazy-video-wrapper {
    .play-overlay {
      .play-button {
        width: 48px;
        height: 48px;
        
        i {
          font-size: 20px;
        }
      }
    }
    
    .lazy-loading-overlay {
      .loading-spinner .spinner-ring {
        width: 24px;
        height: 24px;
        border-width: 2px;
      }
      
      .loading-text {
        font-size: 12px;
      }
    }
    
    .lazy-error-overlay {
      .error-icon i {
        font-size: 36px;
      }
      
      .error-text {
        font-size: 12px;
      }
      
      .retry-button {
        padding: 6px 12px;
        font-size: 11px;
      }
    }
  }
}
</style> 